import { useCallback } from "react";
import useSWR, { useSWRConfig } from "swr";
import { auth, vceFirestore } from "../../configs/FirebaseConfig";
import {
  FIRESTORE_HISTORY_SUBCOLLECTION,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_PICK_WAVES_TABLE,
} from "../../constants/FirestoreConstant";
import { PICKING_STATUS, WAVE_STATUS } from "../../constants/PickingConstant";
import { buildWaveDocument, splitOrderRef, validateWaveDocument } from "../../functions/waveDocument";
import { commitInChunks, resolveFirestore } from "./shopFirestore";
import { hasHvoSession } from "./useProductFields";

/**
 * Pick rounds ("waves", HVC-48): composing one, cancelling one, and reading them back.
 *
 * A wave is written as one document in VCE `pickWaves` - the contract the app reads
 * (docs/wave-contract-v1.md) - and every order in it is marked in its OWN shop's Firestore with
 * `picking.status = queued`, as dot paths so nothing else on the order is touched. Orders are
 * marked first and the wave document is written last: an order marked for a wave that does not
 * exist would never be offered for a round again, so when marking fails halfway the marks that
 * did land are taken off again and nothing is left behind.
 */

const wavesCollection = () => vceFirestore.collection(FIRESTORE_PICK_WAVES_TABLE);

export const fetchWaves = async (pickDay) => {
  // One equality filter and a sort in memory: no composite index needed, a day has few waves
  const snapshot = await wavesCollection().where("pickDay", "==", pickDay).get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
};

/** Open (waiting for a scanner) or busy (being picked): a picker has, or is about to get, its orders. */
export const isActiveWave = (wave) => wave.status === WAVE_STATUS.OPEN || wave.status === WAVE_STATUS.BUSY;

/**
 * The active rounds of a day, read now. For guards: the list on screen can be stale or can have
 * failed to load, and a guard that reads "no rounds" from a failed read lets the action through.
 * This one throws instead.
 */
export const fetchActiveWaves = async (pickDay) => (await fetchWaves(pickDay)).filter(isActiveWave);

const currentEmail = () => (auth.currentUser && auth.currentUser.email) || null;
const currentUid = () => (auth.currentUser && auth.currentUser.uid) || null;

const groupByShop = (items, shopOf) =>
  items.reduce((groups, item) => {
    const shop = shopOf(item);
    return { ...groups, [shop]: [...(groups[shop] || []), item] };
  }, {});

const historyReference = (firestore, docId) =>
  firestore.collection(FIRESTORE_ORDERS_TABLE).doc(docId).collection(FIRESTORE_HISTORY_SUBCOLLECTION).doc();

const NO_WAVES = [];

export const usePickWaves = (pickDay) => {
  const { mutate: globalMutate } = useSWRConfig();
  const { data, error, isLoading, mutate } = useSWR(pickDay ? ["pickWaves", pickDay] : null, () => fetchWaves(pickDay), {
    refreshInterval: 15000,
    revalidateOnFocus: true,
    dedupingInterval: 5000,
  });

  /** The orders page and the pick day panel read orders through their own SWR keys. */
  const revalidateOrders = useCallback(
    () => globalMutate((key) => Array.isArray(key) && (key[0] === "orders" || key[0] === "pickDayOrders")),
    [globalMutate],
  );

  /**
   * Compose a round.
   *
   * @param {Object} input
   * @param {Object|null} input.pickDayDoc - must be released: a round is a snapshot of a locked day
   * @param {Object[]} input.orders - the chosen orders, in the order they go on the cart
   * @param {Map} input.productIndex, input.pickRouteIndex
   * @param {Object[]} input.links - productLinks
   * @param {string[]} input.loadedShops - catalogues that actually loaded
   * @param {Function} input.nextWaveNumber - from usePickDays
   * @returns {{ok: true, wave, refused, warnings} | {ok: false, reason, ...}}
   */
  const createWave = useCallback(
    async ({ pickDay: day, gapDays, deliveryDates, pickDayDoc, orders, productIndex, pickRouteIndex, links, loadedShops, nextWaveNumber }) => {
      if (!pickDayDoc || pickDayDoc.status !== "RELEASED") return { ok: false, reason: "wave/day-not-released" };
      if (!orders || orders.length === 0) return { ok: false, reason: "wave/no-orders" };
      const shops = [...new Set(orders.map((order) => order.shopSource))];
      const notLoaded = shops.filter((shop) => !(loadedShops || []).includes(shop));
      if (notLoaded.length > 0) return { ok: false, reason: "wave/catalogue-not-loaded", shops: notLoaded };
      if (shops.includes("HVO") && !hasHvoSession()) return { ok: false, reason: "wave/no-hvo-session" };
      const taken = orders.filter((order) => order.picking && order.picking.waveId);
      if (taken.length > 0) return { ok: false, reason: "wave/already-in-wave", orderIds: taken.map((order) => order.orderId) };

      const reference = wavesCollection().doc();
      const waveNumber = await nextWaveNumber();
      const now = Date.now();
      const built = buildWaveDocument({
        waveId: reference.id,
        waveNumber,
        pickDay: day,
        gapDays,
        deliveryDates,
        orders,
        productIndex,
        pickRouteIndex,
        links,
        createdBy: currentEmail(),
        now,
      });
      if (!built.ok) return { ok: false, reason: "wave/nothing-mappable", refused: built.refused };
      const validation = validateWaveDocument(built.wave);
      if (!validation.ok) return { ok: false, reason: "wave/invalid", problems: validation.problems };

      // 1. Mark the orders, shop by shop
      const byShop = groupByShop(built.wave.orders, (waveOrder) => waveOrder.shopSource);
      const marked = [];
      try {
        for (const [shop, waveOrders] of Object.entries(byShop)) {
          const firestore = resolveFirestore(shop);
          await commitInChunks(
            firestore,
            waveOrders.flatMap((waveOrder) => {
              const { docId } = splitOrderRef(waveOrder.orderRef);
              return [
                (batch) =>
                  batch.update(firestore.collection(FIRESTORE_ORDERS_TABLE).doc(docId), {
                    "picking.status": PICKING_STATUS.QUEUED,
                    "picking.waveId": built.wave.waveId,
                    "picking.waveNumber": waveNumber,
                    "picking.waveSequence": waveOrder.sequence,
                    "picking.pickDay": day,
                    "picking.queuedAt": now,
                  }),
                (batch) =>
                  batch.set(historyReference(firestore, docId), {
                    timestamp: now,
                    adminEmail: currentEmail(),
                    adminUid: currentUid(),
                    action: "wave_queued",
                    details: { waveId: built.wave.waveId, waveNumber, pickDay: day, sequence: waveOrder.sequence },
                  }),
              ];
            }),
          );
          marked.push(...waveOrders);
        }
      } catch (markError) {
        // Take off what did land, so no order points at a wave that was never written
        await Promise.all(
          marked.map((waveOrder) => {
            const { shopSource, docId } = splitOrderRef(waveOrder.orderRef);
            return resolveFirestore(shopSource)
              .collection(FIRESTORE_ORDERS_TABLE)
              .doc(docId)
              .update({ picking: null })
              .catch(() => null);
          }),
        );
        return { ok: false, reason: "wave/mark-failed", message: markError.message };
      }

      // 2. The wave itself, last
      await reference.set(built.wave);
      await mutate();
      await revalidateOrders();
      return { ok: true, wave: built.wave, refused: built.refused, warnings: built.warnings };
    },
    [mutate, revalidateOrders],
  );

  /**
   * Cancel a round that nobody has started. Its orders become available again; anything already
   * picked is never undone here - a started round is finished or aborted on the scanner.
   */
  const cancelWave = useCallback(
    async (wave) => {
      if (!wave || wave.status !== WAVE_STATUS.OPEN) return { ok: false, reason: "wave/not-open" };
      const byShop = groupByShop(wave.orders || [], (waveOrder) => waveOrder.shopSource);
      const now = Date.now();
      for (const [shop, waveOrders] of Object.entries(byShop)) {
        const firestore = resolveFirestore(shop);
        const operations = [];
        for (const waveOrder of waveOrders) {
          const { docId } = splitOrderRef(waveOrder.orderRef);
          const reference = firestore.collection(FIRESTORE_ORDERS_TABLE).doc(docId);
          const snapshot = await reference.get();
          const picking = snapshot.exists ? snapshot.data().picking : null;
          // Only clear what is still ours and untouched
          if (!picking || picking.waveId !== wave.waveId || picking.status !== PICKING_STATUS.QUEUED) continue;
          operations.push((batch) => batch.update(reference, { picking: null }));
          operations.push((batch) =>
            batch.set(historyReference(firestore, docId), {
              timestamp: now,
              adminEmail: currentEmail(),
              adminUid: currentUid(),
              action: "wave_cancelled",
              details: { waveId: wave.waveId, waveNumber: wave.waveNumber || null },
            }),
          );
        }
        if (operations.length > 0) await commitInChunks(firestore, operations);
      }
      await wavesCollection().doc(wave.waveId || wave.id).update({
        status: WAVE_STATUS.CANCELLED,
        cancelledAt: now,
        cancelledBy: currentEmail(),
        updatedAt: now,
      });
      await mutate();
      await revalidateOrders();
      return { ok: true };
    },
    [mutate, revalidateOrders],
  );

  /** The current order documents of a wave, in wave order - for printing its packing slips. */
  const fetchWaveOrders = useCallback(async (wave) => {
    const orders = await Promise.all(
      (wave.orders || []).map(async (waveOrder) => {
        const { shopSource, docId } = splitOrderRef(waveOrder.orderRef);
        const snapshot = await resolveFirestore(shopSource).collection(FIRESTORE_ORDERS_TABLE).doc(docId).get();
        if (!snapshot.exists) return null;
        return {
          id: docId,
          ...snapshot.data(),
          shopSource,
          shopName: shopSource === "VCE" ? "Vleescentrum" : "Halalvlees Online",
        };
      }),
    );
    return orders.filter(Boolean);
  }, []);

  const markPrinted = useCallback(
    async (wave) => {
      await wavesCollection().doc(wave.waveId || wave.id).update({ printedAt: Date.now(), printedBy: currentEmail(), updatedAt: Date.now() });
      await mutate();
    },
    [mutate],
  );

  return {
    waves: data || NO_WAVES,
    activeWaves: (data || NO_WAVES).filter(isActiveWave),
    isLoading,
    error,
    refresh: mutate,
    createWave,
    cancelWave,
    fetchWaveOrders,
    markPrinted,
  };
};
